import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

import { getRolePath } from '../utils/common';
import roleBasedReducers from '../utils/role-based-reducers';
import { injectReducer } from '../redux/store';

// Import all role-based pages
import {
  AdminRoutes,
  PurchaseHeadRoutes,
  SalesExecutiveRoutes,
  warehouseManagerRoutes,
  FarmSupervisorRoutes,
  FarmPurchaseManagerRoutes,
  ReceivingManagerRoutes,
  QCManagerRoutes,
  ProductionManagerRoutes,
  PackagingSupervisorRoutes,
  DispatchExecutiveRoutes,
  FactoryPurchaseManagerRoutes,
  GatekeeperRoutes,
  QCExecutiveRoutes,
} from './roleRoutes';

const roleBasedRoutes = {
  'Admin': AdminRoutes,
  'Sales Executive': SalesExecutiveRoutes,
  'Warehouse Manager': warehouseManagerRoutes,
  'Purchase Head': PurchaseHeadRoutes,
  'Farm Supervisor': FarmSupervisorRoutes,
  'Farm Purchase Manager': FarmPurchaseManagerRoutes,
  'Receiving Manager': ReceivingManagerRoutes,
  'QC Manager': QCManagerRoutes,
  'QC Executive': QCExecutiveRoutes,
  'Production Manager': ProductionManagerRoutes,
  'Packaging Supervisor': PackagingSupervisorRoutes,
  'Dispatch Executive': DispatchExecutiveRoutes,
  'Factory Purchase Manager': FactoryPurchaseManagerRoutes,
  'Gate Keeper': GatekeeperRoutes,
};

const PageLoader = lazy(() => import('../components/loader/PageLoader'));
const LoginPage = lazy(() => import('../pages/common/auth/LoginPage'));
const NotFoundPage = lazy(() => import('../pages/common/not-found/NotFoundPage'));
const MainLayout = lazy(() => import('../layouts/common-layout/MainLayout'));

const AppRoutes = () => {
  const { user } = useSelector((state) => state.auth);

  const isAuthenticated = !!user;
  const userRole = user?.role;
  let role = getRolePath(userRole); // Converts sales_executive → sales-executive
 
  if (isAuthenticated && userRole) {
    const reducersToInject = roleBasedReducers[user.role];
    if (reducersToInject) {
      Object.entries(reducersToInject).forEach(([key, reducer]) => {
        injectReducer(key, reducer);
      });
    }
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route element={<PublicRoute />}>
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Protected routes nested inside ProtectedRoute */}
        {isAuthenticated && roleBasedRoutes[user.role] && (
          <Route element={<ProtectedRoute />}>
            {roleBasedRoutes[user.role].map(({ path, element, title }, i) => {
              const fullPath = `/${role}/${path}`;
              return (
                <Route
                  key={i}
                  path={fullPath}
                  element={<MainLayout title={title}>{element}</MainLayout>}
                />
              );
            })}

            {/* Dashboard default route */}
            <Route index element={<Navigate to={`/${role}/dashboard`} />} />
          </Route>
        )}

        {/* Redirect root based on role */}
        <Route path="/" element={
          user ? (
            <Navigate to={`/${role}/dashboard`} />
          ) : (
            <Navigate to="/login" />
          )
        }
        />

        {/* Catch-all - redirect to login if not authenticated, otherwise show 404 */}
        <Route path="*" element={
          isAuthenticated ? <NotFoundPage /> : <Navigate to="/login" replace />
        } />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes;